"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/rac/button";
import facingsData from "@/info/facings";
import { CheckIcon, X } from "lucide-react";
import { useEffect, useState } from "react";
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import { Popover } from "./rac/popover";
import { Input } from "./rhf/input";
interface PlotSideDetail {
  dimension: string | number;
  isSideRoadFacing: "yes" | "no" | "";
  roadWidth?: string | number;
  facing?: string;
}
interface PlotDimensionsData {
  plotDimensions: {
    plotSides: number;
    details: PlotSideDetail[];
  };
}
interface PlotDimensionsFormPageProps {
  usePopover?: boolean;
}
const defaultMetaList = [{
  name: "dimension",
  label: "Length of side (ft)",
  type: "number"
},
// required
{
  name: "isSideRoadFacing",
  // required
  label: "Is it facing a road?",
  type: "radio",
  options: [{
    label: "Yes",
    value: "yes"
  }, {
    label: "No",
    value: "no"
  }]
}, {
  name: "roadWidth",
  // required if isSideRoadFacing === "yes"
  label: "Width of the road (ft)",
  type: "number"
}, {
  name: "facing",
  // required if isSideRoadFacing === "yes"
  label: "Facing",
  type: "buttongroup",
  expandable: true,
  options: facingsData.map(f => ({
    label: f.name,
    value: f.code
  })),
  itemsClassName: "grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6",
  description: "In which direction is the road from the plot"
}];
interface FieldMeta {
  name: string;
  label: string;
  type: string;
  options?: Array<{
    label: string;
    value: string;
  }>;
  expandable?: boolean;
  itemsClassName?: string;
  description?: string;
}
interface SideValue {
  [key: string]: unknown;
  dimension?: string | number;
  isSideRoadFacing?: "yes" | "no" | "";
  roadWidth?: string | number;
  facing?: string;
}

/**
 * Checks if a side configuration is complete based on the required fields
 * @param value - The side configuration object
 * @param metaList - Array of field metadata defining which fields are required
 * @returns boolean indicating if the side is complete
 */
function isSideComplete(value: SideValue = {}, metaList: Array<FieldMeta>) {
  return metaList.every(({
    name: fieldName
  }) => {
    const fieldValue = value[fieldName];

    // Skip conditional fields if not required
    if (fieldName === "roadWidth" && value.isSideRoadFacing !== "yes") return true;
    if (fieldName === "facing" && value.isSideRoadFacing !== "yes") return true;

    // Required check: value should not be null or undefined (allow 0, false, etc.)
    const isFilled = fieldValue !== undefined && fieldValue !== null && fieldValue !== "";
    return isFilled;
  });
}
interface SideTabsProps {
  activeTab: number;
  setActiveTab: (i: number) => void;
  metaList: Array<FieldMeta>;
  plotSides: number;
  canSwitchTab: (i: number) => boolean;
  details: Array<SideValue>;
}

/**
 * Component for rendering navigation tabs for each side of the plot
 * Responsive: Grid layout on mobile, vertical stack on desktop
 */

const SideTabs = t0 => {
  const $ = _c(13);
  const {
    activeTab,
    setActiveTab,
    metaList,
    details,
    plotSides,
    canSwitchTab
  } = t0;
  let t1;
  if ($[0] !== activeTab || $[1] !== canSwitchTab || $[2] !== details || $[3] !== metaList || $[4] !== plotSides || $[5] !== setActiveTab) {
    let t2;
    if ($[7] !== activeTab || $[8] !== canSwitchTab || $[9] !== details || $[10] !== metaList || $[11] !== setActiveTab) {
      t2 = (_, i) => {
        const isComplete = isSideComplete(details[i] || {}, metaList);
        const isActive = activeTab === i;
        const canSwitch = canSwitchTab(i);
        return <button key={i} type="button" aria-label={`Configure Side ${i + 1}${isComplete ? " (completed)" : ""}`} aria-current={isActive ? "step" : undefined} onClick={() => canSwitch && setActiveTab(i)} className={`flex items-center justify-between px-2.5 py-1.5 border rounded-lg transition-all duration-200 w-full touch-manipulation h-9
						md:px-3 md:py-2 md:rounded-lg md:h-10
						${isActive ? "bg-primary text-primary-foreground border-primary shadow-sm" : "bg-card border-border text-card-foreground hover:bg-muted hover:border-border/80"} 
						${!canSwitch ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:shadow-sm"}
					`} disabled={!canSwitch}><span className="text-xs font-medium md:text-sm">Side {i + 1}</span><span className="flex justify-end min-w-[1rem]">{isComplete ? <CheckIcon className="w-3.5 h-3.5 text-success" aria-hidden="true" /> : <span className="w-3.5 h-3.5" aria-hidden="true" />}</span></button>;
      };
      $[7] = activeTab;
      $[8] = canSwitchTab;
      $[9] = details;
      $[10] = metaList;
      $[11] = setActiveTab;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    t1 = <div className="grid grid-cols-2 gap-2 w-full sm:grid-cols-3 md:flex md:flex-col md:w-auto md:gap-1.5 lg:gap-2">{Array.from({
        length: plotSides
      }, t2)}</div>;
    $[0] = activeTab;
    $[1] = canSwitchTab;
    $[2] = details;
    $[3] = metaList;
    $[4] = plotSides;
    $[5] = setActiveTab;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
interface SideDetailsProps {
  sideIndex: number;
  baseName: string;
  metaList: FieldMeta[];
  onClear: (i: number) => void;
}

/**
 * Component for rendering the form fields for a specific side
 * Handles conditional field display and validation based on "Is it facing a road" selection
 */

const SideDetails = t0 => {
  const $ = _c(73);
  const {
    sideIndex,
    baseName,
    metaList,
    onClear
  } = t0;
  const {
    control,
    trigger,
    clearErrors,
    setValue
  } = useFormContext();
  const sidePath = `${baseName}.details[${sideIndex}]`;
  const t1 = `${sidePath}.isSideRoadFacing`;
  let t2;
  if ($[0] !== control || $[1] !== t1) {
    t2 = {
      name: t1,
      control
    };
    $[0] = control;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isSideRoadFacing = useWatch(t2);
  let t3;
  if ($[3] !== metaList || $[4] !== onClear || $[5] !== sideIndex || $[6] !== sidePath || $[7] !== trigger) {
    t3 = () => {
      onClear(sideIndex);
      setTimeout(() => {
        trigger(metaList.map(field => `${sidePath}.${field.name}`));
      }, 0);
    };
    $[3] = metaList;
    $[4] = onClear;
    $[5] = sideIndex;
    $[6] = sidePath;
    $[7] = trigger;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleClearAndValidate = t3;
  let t4;
  let t5;
  if ($[9] !== clearErrors || $[10] !== isSideRoadFacing || $[11] !== setValue || $[12] !== sidePath) {
    t4 = () => {
      if (isSideRoadFacing !== "yes") {
        setValue(`${sidePath}.roadWidth`, "", {
          shouldDirty: true
        });
        setValue(`${sidePath}.facing`, "", {
          shouldDirty: true
        });
        clearErrors([`${sidePath}.roadWidth`, `${sidePath}.facing`]);
      }
    };
    t5 = [isSideRoadFacing, clearErrors, setValue, sidePath];
    $[9] = clearErrors;
    $[10] = isSideRoadFacing;
    $[11] = setValue;
    $[12] = sidePath;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[15] !== isSideRoadFacing || $[16] !== sidePath || $[17] !== trigger) {
    t6 = () => {
      if (isSideRoadFacing === "yes") {
        setTimeout(() => {
          trigger([`${sidePath}.roadWidth`, `${sidePath}.facing`]);
        }, 0);
      }
    };
    t7 = [isSideRoadFacing, trigger, sidePath];
    $[15] = isSideRoadFacing;
    $[16] = sidePath;
    $[17] = trigger;
    $[18] = t6;
    $[19] = t7;
  } else {
    t6 = $[18];
    t7 = $[19];
  }
  useEffect(t6, t7);
  let t8;
  if ($[20] !== metaList) {
    t8 = metaList.find(_temp);
    $[20] = metaList;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const dimensionField = t8;
  let t9;
  if ($[22] !== metaList) {
    t9 = metaList.find(_temp2);
    $[22] = metaList;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const isSideRoadFacingField = t9;
  let t10;
  if ($[24] !== metaList) {
    t10 = metaList.find(_temp3);
    $[24] = metaList;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const roadWidthField = t10;
  let t11;
  if ($[26] !== metaList) {
    t11 = metaList.find(_temp4);
    $[26] = metaList;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const facingField = t11;
  let t12;
  if ($[28] !== isSideRoadFacing || $[29] !== metaList) {
    t12 = fieldName => {
      const isRequired = fieldName === "roadWidth" || fieldName === "facing" ? isSideRoadFacing === "yes" : true;
      return isRequired ? {
        required: {
          value: true,
          message: `${metaList.find(f_3 => f_3.name === fieldName)?.label} is required`
        }
      } : undefined;
    };
    $[28] = isSideRoadFacing;
    $[29] = metaList;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const createFieldRules = t12;
  const t13 = sideIndex + 1;
  let t14;
  if ($[31] !== t13) {
    t14 = <h4 className="text-sm font-semibold text-card-foreground md:text-base">Details for Side {t13}</h4>;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const t15 = `Clear Side ${sideIndex + 1}`;
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <X className="w-4 h-4 text-destructive" aria-hidden="true" />;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== handleClearAndValidate || $[35] !== t15) {
    t17 = <Button type="button" variant="ghost" size="sm" onClick={handleClearAndValidate} aria-label={t15} className="ml-2">{t16}</Button>;
    $[34] = handleClearAndValidate;
    $[35] = t15;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t14 || $[38] !== t17) {
    t18 = <div className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border md:p-4">{t14}{t17}</div>;
    $[37] = t14;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const t19 = `${sidePath}.dimension`;
  let t20;
  if ($[40] !== createFieldRules) {
    t20 = createFieldRules("dimension");
    $[40] = createFieldRules;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== control || $[43] !== dimensionField || $[44] !== t19 || $[45] !== t20) {
    t21 = <div><Input {...dimensionField} name={t19} control={control} rules={t20} labelClassName="text-xs font-medium text-neutral-800 px-3" /></div>;
    $[42] = control;
    $[43] = dimensionField;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const t22 = `${sidePath}.isSideRoadFacing`;
  let t23;
  if ($[47] !== createFieldRules) {
    t23 = createFieldRules("isSideRoadFacing");
    $[47] = createFieldRules;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== control || $[50] !== isSideRoadFacingField || $[51] !== t22 || $[52] !== t23) {
    t24 = <div><Input {...isSideRoadFacingField} name={t22} control={control} rules={t23} /></div>;
    $[49] = control;
    $[50] = isSideRoadFacingField;
    $[51] = t22;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t21 || $[55] !== t24) {
    t25 = <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:gap-4">{t21}{t24}</div>;
    $[54] = t21;
    $[55] = t24;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== control || $[58] !== createFieldRules || $[59] !== facingField || $[60] !== isSideRoadFacing || $[61] !== roadWidthField || $[62] !== sidePath) {
    t26 = isSideRoadFacing === "yes" && <div className="space-y-2"><div className="text-xs font-medium text-card-foreground flex items-center gap-2 px-3"><div className="w-1 h-3 bg-primary rounded-full" />Road Details</div><div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:gap-4 p-3 bg-muted/30 rounded-lg border border-dashed border-border"><div><Input {...roadWidthField} name={`${sidePath}.roadWidth`} control={control} rules={createFieldRules("roadWidth")} labelClassName="text-xs font-medium text-neutral-800 px-3" /></div><div><Input {...facingField} name={`${sidePath}.facing`} control={control} rules={createFieldRules("facing")} itemsClassName="grid grid-cols-2 gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" /></div></div></div>;
    $[57] = control;
    $[58] = createFieldRules;
    $[59] = facingField;
    $[60] = isSideRoadFacing;
    $[61] = roadWidthField;
    $[62] = sidePath;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== isSideRoadFacing) {
    t27 = isSideRoadFacing === "no" && <div className="text-xs text-muted-foreground italic text-center py-1 px-3">This side doesn't face a road</div>;
    $[64] = isSideRoadFacing;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== t25 || $[67] !== t26 || $[68] !== t27) {
    t28 = <div className="space-y-3 md:space-y-4">{t25}{t26}{t27}</div>;
    $[66] = t25;
    $[67] = t26;
    $[68] = t27;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== t18 || $[71] !== t28) {
    t29 = <div className="space-y-3 w-full md:space-y-4">{t18}{t28}</div>;
    $[70] = t18;
    $[71] = t28;
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  return t29;
};
export const PlotSidesForm = t0 => {
  const $ = _c(38);
  const {
    name,
    metaList: t1,
    label: t2
  } = t0;
  const metaList = t1 === undefined ? defaultMetaList : t1;
  const label = t2 === undefined ? "No. of Plot Sides (with Dimensions)" : t2;
  const [activeTab, setActiveTab] = useState(0);
  const {
    control,
    setValue,
    getValues,
    trigger
  } = useFormContext();
  const plotSides = useWatch({
    control,
    name: `${name}.plotSides`
  }) || 0;
  const details = useWatch({
    control,
    name: `${name}.details`
  }) || [];
  let t3;
  if ($[0] !== metaList || $[1] !== name || $[2] !== trigger) {
    t3 = i => {
      setActiveTab(i);
      setTimeout(() => {
        trigger(metaList.map(field => `${name}.details[${i}].${field.name}`));
      }, 0);
    };
    $[0] = metaList;
    $[1] = name;
    $[2] = trigger;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const setActiveTabFunc = t3;
  let t4;
  let t5;
  if ($[4] !== activeTab || $[5] !== getValues || $[6] !== name || $[7] !== plotSides || $[8] !== setValue) {
    t4 = () => {
      if (plotSides > 0) {
        let iDetails = getValues(`${name}.details`);
        if (!Array.isArray(iDetails) || iDetails.length !== plotSides) {
          iDetails = Array.from({
            length: plotSides
          }, (_, i_0) => ({
            dimension: "",
            isSideRoadFacing: "",
            roadWidth: "",
            facing: "",
            ...(iDetails && iDetails[i_0] ? iDetails[i_0] : {})
          }));
          setValue(`${name}.details`, iDetails, {
            shouldDirty: true
          });
        }
        if (activeTab >= plotSides) {
          setActiveTab(0);
        }
      }
    };
    t5 = [plotSides, name, setValue, getValues, activeTab];
    $[4] = activeTab;
    $[5] = getValues;
    $[6] = name;
    $[7] = plotSides;
    $[8] = setValue;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  if ($[11] !== details || $[12] !== metaList || $[13] !== name || $[14] !== setValue || $[15] !== trigger) {
    t6 = i_1 => {
      const newDetails = [...details];
      newDetails[i_1] = {
        dimension: "",
        isSideRoadFacing: "",
        roadWidth: "",
        facing: ""
      };
      setValue(`${name}.details`, newDetails, {
        shouldDirty: true
      });
      setTimeout(() => {
        trigger(metaList.map(field_0 => `${name}.details[${i_1}].${field_0.name}`));
      }, 0);
    };
    $[11] = details;
    $[12] = metaList;
    $[13] = name;
    $[14] = setValue;
    $[15] = trigger;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const handleClear = t6;
  let t7;
  if ($[17] !== activeTab || $[18] !== details || $[19] !== metaList) {
    t7 = i_2 => {
      for (let idx = 0; idx < i_2; idx++) {
        if (!isSideComplete(details[idx] || {}, metaList)) {
          return false;
        }
      }
      if (!isSideComplete(details[activeTab] || {}, metaList)) {
        return false;
      }
      return true;
    };
    $[17] = activeTab;
    $[18] = details;
    $[19] = metaList;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const canSwitchTab = t7;
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = Array.from({
      length: 6
    }, _temp5);
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = `${name}.plotSides`;
  let t10;
  if ($[22] !== control || $[23] !== label || $[24] !== t9) {
    t10 = <div className="mb-4 md:mb-6"><Input type="buttongroup" control={control} initialCount={4} label={label} options={t8} name={t9} placeholder="Select number of sides" expandable={true} /></div>;
    $[22] = control;
    $[23] = label;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== activeTab || $[27] !== canSwitchTab || $[28] !== details || $[29] !== handleClear || $[30] !== metaList || $[31] !== name || $[32] !== plotSides || $[33] !== setActiveTabFunc) {
    t11 = plotSides > 0 && <div className="flex flex-col gap-3 md:flex-row md:gap-4 lg:gap-6"><div className="md:w-auto md:min-w-[160px] lg:min-w-[180px]"><SideTabs activeTab={activeTab} plotSides={plotSides} setActiveTab={setActiveTabFunc} metaList={metaList} canSwitchTab={canSwitchTab} details={details} /></div><div className="flex-1 min-w-0"><SideDetails onClear={handleClear} key={activeTab} sideIndex={activeTab} baseName={name} metaList={metaList} /></div></div>;
    $[26] = activeTab;
    $[27] = canSwitchTab;
    $[28] = details;
    $[29] = handleClear;
    $[30] = metaList;
    $[31] = name;
    $[32] = plotSides;
    $[33] = setActiveTabFunc;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t10 || $[36] !== t11) {
    t12 = <div className="mt-4 md:mt-6">{t10}{t11}</div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  return t12;
};
const PlotDimensionsFormPage = t0 => {
  const $ = _c(15);
  const {
    usePopover: t1
  } = t0;
  const usePopover = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      defaultValues: {
        plotDimensions: {
          plotSides: 4,
          details: [{
            dimension: 1234,
            isSideRoadFacing: "yes",
            roadWidth: 120,
            facing: "north"
          }, {
            dimension: 5678,
            isSideRoadFacing: "no"
          }]
        }
      },
      mode: "onChange"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const methods = useForm(t2);
  const {
    handleSubmit
  } = methods;
  const onSubmit = _temp6;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <a href="#plot-dimensions-form" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-primary text-primary-foreground px-4 py-2 rounded-md z-50">Skip to form</a>;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== handleSubmit) {
    t4 = handleSubmit(onSubmit);
    $[2] = handleSubmit;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <header className="text-center md:text-left"><h1 id="plot-dimensions-title" className="text-lg font-bold text-card-foreground md:text-xl lg:text-2xl">Plot Dimensions</h1><p className="mt-1 text-xs text-muted-foreground md:text-sm">Configure the dimensions and road-facing details for each side of your plot</p></header>;
    t6 = <PlotSidesForm name="plotDimensions" metaList={defaultMetaList} />;
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <footer className="flex justify-center pt-3 md:justify-start md:pt-4"><Button type="submit" variant="default" size="lg" className="w-full h-12 font-bold sm:w-auto sm:min-w-[120px] md:min-w-[140px]">Submit Form</Button></footer>;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t4) {
    t8 = <main className={"p-4 max-w-2xl mx-auto space-y-4 bg-card rounded-xl shadow-card border border-border\n\t\t\t\tsm:p-5 sm:space-y-5 sm:max-w-3xl\n\t\t\t\tmd:p-6 md:max-w-4xl md:space-y-6\n\t\t\t\tlg:max-w-5xl lg:p-8\n\t\t\t\txl:max-w-6xl"} role="main" aria-labelledby="plot-dimensions-title">{t3}<form id="plot-dimensions-form" onSubmit={t4} className="space-y-4 md:space-y-5" aria-label="Plot Dimensions Form" noValidate={true}>{t5}{t6}{t7}</form></main>;
    $[7] = t4;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const content = t8;
  let t9;
  if ($[9] !== content || $[10] !== usePopover) {
    t9 = usePopover ? <Popover>{content}</Popover> : content;
    $[9] = content;
    $[10] = usePopover;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== methods || $[13] !== t9) {
    t10 = <FormProvider {...methods}>{t9}</FormProvider>;
    $[12] = methods;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  return t10;
};
export default PlotDimensionsFormPage;
function _temp(f) {
  return f.name === "dimension";
}
function _temp2(f_0) {
  return f_0.name === "isSideRoadFacing";
}
function _temp3(f_1) {
  return f_1.name === "roadWidth";
}
function _temp4(f_2) {
  return f_2.name === "facing";
}
function _temp5(__0, i_3) {
  return {
    label: `${i_3 + 3}`,
    value: i_3 + 3
  };
}
function _temp6(data) {
  const sides = data.plotDimensions.plotSides;
  const details = data.plotDimensions.details;
  const errors = [];
  for (let i = 0; i < sides; i++) {
    const side = details?.[i];
    if (!side?.dimension) {
      errors.push(`Side ${i + 1}: Length is required`);
    }
    if (!side?.isSideRoadFacing) {
      errors.push(`Side ${i + 1}: Road facing status is required`);
    }
    if (side?.isSideRoadFacing === "yes") {
      if (!side.roadWidth) {
        errors.push(`Side ${i + 1}: Road width is required`);
      }
      if (!side.facing) {
        errors.push(`Side ${i + 1}: Facing is required`);
      }
    }
  }
  if (errors.length > 0) {
    alert("Please fix the following:\n" + errors.join("\n"));
    return;
  }
  alert("Form submitted successfully! See console for details.");
  console.log("Form submitted with data:", data);
}